import { formatDistanceToNow } from 'date-fns'

import { Badge } from '@/components/admin/shared/badge'
import {
  daysBetween,
  retrievability,
} from '@/lib/recommendation/retrievability'
import { cn } from '@/lib/utils'
import { getSubtopicRd, rollUpTopicMasteryFromLive } from './_lib/derivations'
import { useRecommendationEngineMock } from './recommendation-engine-context'
import type { SubtopicState, TopicState } from './types'

/**
 * Per-subtopic snapshot used by both the Calculation Log tab and the Progress
 * tab's topic drill-down. Surfaces every field issue #402 calls out:
 *   `mastery`, live `R(t, S)`, `effectiveMastery`, `stability_days`,
 *   `difficulty`, `lastAttemptAt`, `fsrsParamsVersion`.
 *
 * `R(t, S)` is computed live from `lastAttemptAt` so the displayed value
 * matches what the engine would compute *right now* — the stored
 * `retrievability` on the mock subtopic is a snapshot at mock-build time.
 */

/**
 * Mock V1 default — `fsrsParamsVersion` is on `ClientSubtopicState` per
 * schema (`db/schema/recommendation-engine.ts`) but the mock subtopic
 * doesn't carry it. Wire it up when the mock subtopic shape catches up.
 */
const DEFAULT_FSRS_PARAMS_VERSION = 'fsrs-6-default'

interface SnapshotRow {
  topic: TopicState
  subtopic: SubtopicState
  retrievabilityLive: number
  effectiveMasteryLive: number
  isEnabled: boolean
  fsrsParamsVersion: string
}

function buildSnapshotRows(
  topics: readonly TopicState[],
  now: Date,
): SnapshotRow[] {
  const rows: SnapshotRow[] = []
  for (const topic of topics) {
    for (const subtopic of topic.subtopics) {
      const elapsedDays = daysBetween(subtopic.lastAttemptAt, now)
      const r = retrievability(elapsedDays, subtopic.stabilityDays)
      rows.push({
        topic,
        subtopic,
        retrievabilityLive: r,
        effectiveMasteryLive: Math.round(subtopic.mastery * r),
        isEnabled: subtopic.isEnabled !== false,
        fsrsParamsVersion: DEFAULT_FSRS_PARAMS_VERSION,
      })
    }
  }
  return rows
}

function bucketBadge(topic: TopicState, mastery: number) {
  if (!topic.prerequisitesMet) return <Badge variant="archived">Gated</Badge>
  if (mastery >= topic.maxProficiencyThreshold) {
    return <Badge variant="topic">Mastered</Badge>
  }
  if (mastery >= topic.minProficiencyThreshold) {
    return <Badge variant="topic">Proficient</Badge>
  }
  return <Badge variant="paused">Learning</Badge>
}

function formatLastAttempt(iso: string | null): string {
  if (!iso) return 'Never'
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true })
  } catch {
    return iso
  }
}

interface KnowledgeMapSnapshotProps {
  /**
   * Which slice to render:
   * - 'all' (default) — every topic, grouped by topic header
   * - 'topic' — only `topicId`'s subtopics (no topic header — caller renders it)
   */
  scope?: 'all' | 'topic'
  /** Required when `scope='topic'`. */
  topicId?: string
  /**
   * Fixed `now` for deterministic rendering in tests. Defaults to
   * `new Date()` so `R(t, S)` is live in production.
   */
  now?: Date
  /** Optional className applied to the outer wrapper. */
  className?: string
}

/**
 * `<KnowledgeMapSnapshot>` — read-only per-subtopic FSRS state surface.
 *
 * Used in two places per the #402 plan:
 *  1. Top pane of the Calculation Log tab (scope='all').
 *  2. Topic drill-down on the Progress tab (scope='topic').
 *
 * No mutations — this is purely diagnostic. The seed tool on the **Setup** tab
 * is the surface for *setting* these values.
 */
export function KnowledgeMapSnapshot({
  scope = 'all',
  topicId,
  now,
  className,
}: KnowledgeMapSnapshotProps) {
  const { topics } = useRecommendationEngineMock()
  const referenceNow = now ?? new Date()

  if (scope === 'topic' && !topicId) {
    return (
      <div
        className={cn(
          'text-muted-foreground border-border rounded-lg border border-dashed py-6 text-center text-sm',
          className,
        )}
      >
        No subtopics to display.
      </div>
    )
  }

  const relevantTopics =
    scope === 'topic' && topicId
      ? topics.filter((t) => t.id === topicId)
      : topics

  const rows = buildSnapshotRows(relevantTopics, referenceNow)
  const showTopicGroups = scope === 'all'

  if (rows.length === 0) {
    return (
      <div
        className={cn(
          'text-muted-foreground border-border rounded-lg border border-dashed py-6 text-center text-sm',
          className,
        )}
      >
        No subtopics to display.
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      {showTopicGroups
        ? relevantTopics.map((topic) => (
            <TopicGroup
              key={topic.id}
              topic={topic}
              rows={rows.filter((r) => r.topic.id === topic.id)}
            />
          ))
        : rows.map((row) => (
            <SubtopicRow
              key={row.subtopic.id}
              row={row}
              showTopicName={false}
            />
          ))}
    </div>
  )
}

function TopicGroup({
  topic,
  rows,
}: {
  topic: TopicState
  rows: SnapshotRow[]
}) {
  const liveTopicMastery = rollUpTopicMasteryFromLive(
    rows,
    topic.includedInPlan,
  )
  return (
    <div className="border-border overflow-hidden rounded-lg border">
      <div className="bg-muted/30 flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-foreground text-sm font-semibold">
            {topic.name}
          </h4>
          {bucketBadge(topic, liveTopicMastery)}
          {topic.includedInPlan ? (
            <Badge variant="topic">In plan</Badge>
          ) : (
            <Badge variant="archived">Out of plan</Badge>
          )}
        </div>
        <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-[11px]">
          <span>
            topicMastery{' '}
            <span className="text-foreground tabular-nums">
              {liveTopicMastery}
            </span>
          </span>
          <span>
            range{' '}
            <span className="text-foreground tabular-nums">
              {topic.minProficiencyThreshold} - {topic.maxProficiencyThreshold}
            </span>{' '}
            (R_d = {(topic.maxProficiencyThreshold / 100).toFixed(2)})
          </span>
        </div>
      </div>
      <div className="divide-border divide-y">
        {rows.map((row) => (
          <SubtopicRow key={row.subtopic.id} row={row} showTopicName={false} />
        ))}
      </div>
    </div>
  )
}

function SubtopicRow({
  row,
  showTopicName,
}: {
  row: SnapshotRow
  showTopicName: boolean
}) {
  const {
    topic,
    subtopic,
    retrievabilityLive,
    effectiveMasteryLive,
    isEnabled,
  } = row
  const goal = subtopic.maxProficiencyThreshold ?? topic.maxProficiencyThreshold
  const rd = getSubtopicRd(subtopic, topic)
  return (
    <div className="bg-background grid grid-cols-12 items-center gap-2 px-3 py-2 text-xs">
      <div className="col-span-3">
        <div className="text-foreground font-medium">{subtopic.name}</div>
        {showTopicName && (
          <div className="text-muted-foreground text-[11px]">
            {row.topic.name}
          </div>
        )}
      </div>
      <NumericCol label="mastery" value={subtopic.mastery.toFixed(0)} />
      <NumericCol label="R(t,S)" value={retrievabilityLive.toFixed(2)} />
      <NumericCol
        label="effectiveMastery"
        value={`${effectiveMasteryLive.toFixed(0)} / ${goal}`}
      />
      <NumericCol label="R_d" value={rd.toFixed(2)} />
      <NumericCol
        label="stability"
        value={`${subtopic.stabilityDays.toFixed(0)}d`}
      />
      <NumericCol label="difficulty" value={subtopic.difficulty.toFixed(1)} />
      <div className="text-muted-foreground col-span-2 text-[11px]">
        <div className="text-foreground/80 text-[10px] uppercase tracking-wide">
          last attempt
        </div>
        <div>{formatLastAttempt(subtopic.lastAttemptAt)}</div>
      </div>
      <div className="col-span-1 text-right">
        {isEnabled ? (
          <Badge variant="topic">On</Badge>
        ) : (
          <Badge variant="archived">Off</Badge>
        )}
      </div>
    </div>
  )
}

function NumericCol({ label, value }: { label: string; value: string }) {
  return (
    <div className="col-span-1">
      <div className="text-foreground/80 text-[10px] uppercase tracking-wide">
        {label}
      </div>
      <div className="text-foreground tabular-nums">{value}</div>
    </div>
  )
}
